import { useState } from 'react';
import { Box, SimpleGrid, Stack, Group, Badge, TextInput } from '@mantine/core';
import { IconSearch, IconPlus, IconMail, IconPhone, IconMapPin, IconReceipt } from '@tabler/icons-react';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { mockInvoiceClients, InvoiceClient } from '../mockData/invoicing';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

export default function ClientsPage() {
  const [search, setSearch] = useState('');

  const filteredClients = mockInvoiceClients.filter(client =>
    client.name.toLowerCase().includes(search.toLowerCase()) ||
    client.email.toLowerCase().includes(search.toLowerCase())
  );

  // Sort by total paid descending
  const sortedClients = [...filteredClients].sort((a, b) => b.totalPaid - a.totalPaid);

  const totalOutstanding = mockInvoiceClients.reduce((acc, c) => acc + c.outstandingBalance, 0);
  const totalPaid = mockInvoiceClients.reduce((acc, c) => acc + c.totalPaid, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Clients</Title>
          <Text c="dimmed" size="sm">{mockInvoiceClients.length} clients</Text>
        </Box>
        <Button leftSection={<IconPlus size={16} />}>Add Client</Button>
      </Group>

      {/* Summary Cards */}
      <Group>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total Clients</Text>
          <Text size="xl" fw={700}>{mockInvoiceClients.length}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Total Paid</Text>
          <Text size="xl" fw={700} c="green">{formatCurrency(totalPaid)}</Text>
        </Card>
        <Card style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">Outstanding</Text>
          <Text size="xl" fw={700} c="orange">{formatCurrency(totalOutstanding)}</Text>
        </Card>
      </Group>

      {/* Search */}
      <Card>
        <TextInput
          placeholder="Search clients..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      {/* Clients Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        {sortedClients.map(client => (
          <Card key={client.id}>
            <Stack gap="md">
              <Box>
                <Text fw={600}>{client.name}</Text>
              </Box>

              <Stack gap={4}>
                <Group gap="xs">
                  <IconMail size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">{client.email}</Text>
                </Group>
                <Group gap="xs">
                  <IconPhone size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">{client.phone}</Text>
                </Group>
                <Group gap="xs">
                  <IconMapPin size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed" lineClamp={1}>{client.address}</Text>
                </Group>
              </Stack>

              <Group justify="space-between" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                <Box>
                  <Text size="xs" c="dimmed">Total Paid</Text>
                  <Text size="sm" fw={600} c="green">{formatCurrency(client.totalPaid)}</Text>
                </Box>
                <Box ta="right">
                  <Text size="xs" c="dimmed">Outstanding</Text>
                  <Text size="sm" fw={600} c={client.outstandingBalance > 0 ? 'orange' : 'dimmed'}>
                    {formatCurrency(client.outstandingBalance)}
                  </Text>
                </Box>
              </Group>

              <Group justify="space-between">
                <Group gap="xs">
                  <IconReceipt size={14} color="var(--mantine-color-dimmed)" />
                  <Text size="xs" c="dimmed">{client.totalInvoices} invoices</Text>
                </Group>
                <Button variant="light" size="xs">View Details</Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </SimpleGrid>
    </Stack>
  );
}
